import axios from 'axios';
import type {
  FlashcardDeck,
  ParseFlashcardsRequest,
  ParseFlashcardsResponse,
  AsyncJobResponse,
  ProcessingJob,
  JobStatus,
  RecordAnswerRequest,
  FlashcardStatsResponse
} from '../types/Flashcard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const DEMO_FLASHCARD_STATS_KEY = 'aidivecoach_demo_flashcard_stats';

interface DemoFlashcardStatsRecord {
  [userId: string]: {
    [deckId: string]: {
      [cardId: string]: {
        incorrectCount: number;
        lastAttemptedAt: string;
      };
    };
  };
}

const isLocalOnlyFlashcardUser = (userId: string) => userId === 'guest' || userId.startsWith('demo-');

const loadDemoFlashcardStats = (): DemoFlashcardStatsRecord => {
  try {
    const raw = window.localStorage.getItem(DEMO_FLASHCARD_STATS_KEY);
    return raw ? JSON.parse(raw) as DemoFlashcardStatsRecord : {};
  } catch (error) {
    console.warn('Failed to load demo flashcard stats:', error);
    return {};
  }
};

const saveDemoFlashcardStats = (stats: DemoFlashcardStatsRecord): void => {
  try {
    window.localStorage.setItem(DEMO_FLASHCARD_STATS_KEY, JSON.stringify(stats));
  } catch (error) {
    console.warn('Failed to save demo flashcard stats:', error);
  }
};

const recordDemoAnswer = (request: RecordAnswerRequest): void => {
  const stats = loadDemoFlashcardStats();
  const userStats = stats[request.userId] || {};
  const deckStats = userStats[request.deckId] || {};

  deckStats[request.cardId] = {
    incorrectCount: request.isCorrect ? 0 : 1,
    lastAttemptedAt: new Date().toISOString(),
  };

  userStats[request.deckId] = deckStats;
  stats[request.userId] = userStats;
  saveDemoFlashcardStats(stats);
};

const getDemoStats = (userId: string, deckId: string): FlashcardStatsResponse => {
  const stats = loadDemoFlashcardStats();
  const deckStats = stats[userId]?.[deckId] || {};

  return {
    cardStats: Object.fromEntries(
      Object.entries(deckStats)
        .filter(([_, stat]) => stat.incorrectCount > 0)
        .map(([cardId, stat]) => [cardId, { incorrectCount: stat.incorrectCount }])
    ),
  };
};

export const flashcardService = {
  // Get all global flashcard decks (visible to all users)
  async getAllDecks(): Promise<FlashcardDeck[]> {
    const response = await apiClient.get<FlashcardDeck[]>('/flashcards/decks');
    return response.data;
  },

  // Get a specific global deck
  async getDeck(deckId: string): Promise<FlashcardDeck | null> {
    try {
      const response = await apiClient.get<FlashcardDeck>(`/flashcards/decks/${deckId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch flashcard deck:', error);
      return null;
    }
  },

  // Save a global deck (create or update - visible to all users)
  async saveDeck(deck: FlashcardDeck): Promise<FlashcardDeck> {
    const response = await apiClient.post<FlashcardDeck>('/flashcards/decks', deck);
    return response.data;
  },

  // Delete a global deck
  async deleteDeck(deckId: string): Promise<void> {
    await apiClient.delete(`/flashcards/decks/${deckId}`);
  },

  // Parse content into flashcards
  async parseContent(request: ParseFlashcardsRequest): Promise<ParseFlashcardsResponse> {
    const response = await apiClient.post<ParseFlashcardsResponse>('/flashcards/parse', request);
    return response.data;
  },

  // Parse PDF into flashcards (synchronous - may timeout on large PDFs)
  async parsePdf(file: File): Promise<ParseFlashcardsResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ParseFlashcardsResponse>('/flashcards/parse-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Parse PDF asynchronously - returns jobId for polling
  async parsePdfAsync(file: File, userId: string): Promise<AsyncJobResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<AsyncJobResponse>(
      `/flashcards/parse-pdf-async?userId=${userId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  // Get job status
  async getJobStatus(jobId: string): Promise<ProcessingJob> {
    const response = await apiClient.get<ProcessingJob>(`/flashcards/job/${jobId}`);
    return response.data;
  },

  // Poll job until complete (with callback for status updates)
  // Default: 2s interval * 150 attempts = 5 minutes max (matches Lambda timeout)
  async pollJobStatus(
    jobId: string,
    onStatusUpdate?: (status: JobStatus) => void,
    pollInterval: number = 2000,
    maxAttempts: number = 150
  ): Promise<ParseFlashcardsResponse> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const job = await this.getJobStatus(jobId);

      if (onStatusUpdate) {
        onStatusUpdate(job.status);
      }

      if (job.status === 'Completed' as JobStatus) {
        if (job.resultData) {
          return JSON.parse(job.resultData) as ParseFlashcardsResponse;
        }
        throw new Error('Job completed but no result data available');
      }

      if (job.status === 'Failed' as JobStatus) {
        throw new Error(job.errorMessage || 'Job failed');
      }

      // Still processing, wait and try again
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }

    throw new Error('Job polling timeout - processing taking too long');
  },

  // Record a flashcard answer
  async recordAnswer(request: RecordAnswerRequest): Promise<void> {
    if (isLocalOnlyFlashcardUser(request.userId)) {
      recordDemoAnswer(request);
      return;
    }

    try {
      await apiClient.post('/flashcards/record-answer', request);
    } catch (error) {
      console.warn('Failed to record answer:', error);
      // Don't throw - tracking failures shouldn't break the user experience
    }
  },

  // Get flashcard statistics for a user/deck
  async getStats(userId: string, deckId: string): Promise<FlashcardStatsResponse> {
    if (isLocalOnlyFlashcardUser(userId)) {
      return getDemoStats(userId, deckId);
    }

    try {
      const response = await apiClient.get<FlashcardStatsResponse>(
        `/flashcards/stats/${userId}/${deckId}`
      );
      return response.data;
    } catch (error) {
      console.warn('Failed to get flashcard stats:', error);
      return { cardStats: {} };
    }
  },
};
